/**
 * Datetime Handling Standard - Protoss Frontend
 *
 * Rule: All timestamps from the API are UTC ISO 8601 strings.
 * Rule: All timestamps MUST be converted to user local time before display.
 * Rule: Use Intl.DateTimeFormat / toLocaleString with `undefined` locale so
 *       the browser's configured locale is respected. Never hardcode a locale.
 * Rule: Never render raw UTC strings (e.g. "2026-03-21T20:10:10.068Z") to users.
 * Rule: Do not duplicate formatting logic - always import from this module.
 *
 * See: context/00-core/non-negotiables.md §Datetime Handling Standard
 */

/**
 * Compact datetime for list rows and summary cells.
 * Renders the user's local time. Example: "Mar 21, 2:10 PM"
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Full datetime including seconds, for detail panels and metadata rows.
 * Renders the user's local time. Example: "Mar 21, 2:10:05 PM"
 */
export function formatDateTimeFull(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Log-line timestamp with milliseconds precision, local time.
 * Produces a fixed-width "YYYY-MM-DD HH:mm:ss.mmm" string in local time.
 * Locale-independent by design - log timestamps prioritize precision and
 * machine-readability over locale formatting; the UTC→local conversion still
 * applies so times reflect the user's timezone.
 */
export function formatLogTs(iso: string): string {
  const d = new Date(iso);
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const pad3 = (n: number) => String(n).padStart(3, '0');
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${pad3(d.getMilliseconds())}`
  );
}

/**
 * Date-only display (no time). Uses medium date style.
 * Example: "Mar 21, 2026"
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  // Use noon UTC to avoid date-shift across timezones for date-only strings
  const normalized = iso.length === 10 ? `${iso}T12:00:00Z` : iso;
  return new Date(normalized).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

/**
 * Returns the local timezone abbreviation (e.g. "EDT", "PST", "CEST").
 * Uses Intl.DateTimeFormat to avoid locale-specific token splitting.
 */
export function getLocalTimezoneAbbr(): string {
  return (
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * Converts a date-only input string (YYYY-MM-DD) to a full UTC ISO 8601 string.
 * Uses midnight UTC. Intended for API submissions where only a date is collected.
 */
export function dateInputToUtcIso(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00Z`).toISOString();
}

/**
 * Time-only display in 24h format (HH:mm:ss), local time.
 * Locale-independent - designed for log-line timestamps where precision and
 * fixed-width readability matter over locale formatting.
 * Example: "14:10:05"
 */
export function formatLogTime(iso: string): string {
  const d = new Date(iso);
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/**
 * Time-only display, locale-sensitive 12h/24h per browser locale.
 * Example: "2:10 PM"
 */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Date without year - month and day only.
 * Example: "Mar 21"
 */
export function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Date with short weekday prefix, no year.
 * Example: "Thu, Mar 21"
 */
export function formatDateWithWeekday(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Compact datetime including year, for metadata panels where year context matters.
 * Example: "Mar 21, 2026, 2:10 PM"
 */
export function formatDateTimeWithYear(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format an ISO string as a relative time label ("just now", "5m ago", "2h ago", "3d ago").
 * Falls back to formatDate for dates older than 30 days.
 */
export function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  if (diffSecs < 60) return 'just now';
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatDate(date.toISOString());
}
